import React, { useState, useRef, useEffect } from 'react';
import { Phone, Mail, Video, X, MessageCircle, ExternalLink } from 'lucide-react';

const CONTACT = {
  phone: '+91 7570041003',
  email: 'hi@snigdha360.com',
  meetUrl: 'https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ3bJrffZN5d8HDborg2XclXUXcYZesAtLu05SvdR33ccviTCFfXYOEu7oCkS4MHLhUmS6m7MCin',
};

const FloatingHelpWidget = () => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="fixed bottom-6 right-6 z-50" ref={panelRef} data-testid="floating-help-widget">
      {/* Expanded Panel */}
      <div
        className={`absolute bottom-16 right-0 w-72 origin-bottom-right transition-all duration-300 ease-out ${
          open
            ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 scale-90 translate-y-2 pointer-events-none'
        }`}
      >
        <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-700/60 rounded-2xl shadow-2xl shadow-black/40 overflow-hidden">
          {/* Header */}
          <div className="px-5 pt-5 pb-3">
            <h3 className="text-white font-semibold text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Need Help?
            </h3>
            <p className="text-slate-400 text-xs mt-0.5">We're here to assist you</p>
          </div>

          {/* Contact Options */}
          <div className="px-3 pb-3 space-y-1">
            {/* Phone */}
            <a
              href={`tel:${CONTACT.phone}`}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-800/80 transition-colors duration-200 group"
              data-testid="help-phone-link"
            >
              <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/25 transition-colors">
                <Phone className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium">Call Us</p>
                <p className="text-slate-400 text-xs">{CONTACT.phone}</p>
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            {/* Email */}
            <a
              href={`mailto:${CONTACT.email}`}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-800/80 transition-colors duration-200 group"
              data-testid="help-email-link"
            >
              <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/25 transition-colors">
                <Mail className="w-4 h-4 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium">Email Us</p>
                <p className="text-slate-400 text-xs truncate">{CONTACT.email}</p>
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            {/* Google Meet */}
            <a
              href={CONTACT.meetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-800/80 transition-colors duration-200 group"
              data-testid="help-meet-link"
            >
              <div className="w-9 h-9 rounded-lg bg-purple-500/15 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-500/25 transition-colors">
                <Video className="w-4 h-4 text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium">Book a Meeting</p>
                <p className="text-slate-400 text-xs">Schedule on Google Meet</p>
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        </div>
      </div>

      {/* Trigger Button */}
      <button
        onClick={() => setOpen(!open)}
        className={`w-13 h-13 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 active:scale-95 ${
          open
            ? 'bg-slate-700 hover:bg-slate-600 shadow-slate-900/40'
            : 'bg-emerald-500 hover:bg-emerald-400 shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-105'
        }`}
        style={{ width: 52, height: 52 }}
        data-testid="help-widget-trigger"
        aria-label="Need Help?"
      >
        {open ? (
          <X className="w-5 h-5 text-white" />
        ) : (
          <MessageCircle className="w-5 h-5 text-slate-950" />
        )}
      </button>
    </div>
  );
};

export default FloatingHelpWidget;
